import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpWithCacheService } from '../http-with-cache.service';
import {
  EntityRequirement,
  ITemplateViewModel,
  IServerResponse,
  ProjectTemplate,
  ProjectTemplateTask,
} from '@aksia/models';
import { CedricApiService } from '../../infrastructure/api/cedric.api.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateService extends HttpWithCacheService {
  private api = inject(CedricApiService);
  private controller: string = 'ProjectTemplates';

  constructor() {
    super();
  }

  getProjectTemplateById(id: number): Observable<ProjectTemplate> {
    return this.api
      .post(`${this.controller}/GetProjectTemplate`, { projectTemplateId: id })
      .pipe(
        map((template) => ({
          ...template,
          entityRequirements: template.entityRequirements.map(
            (entity: any) => ({
              ...entity,
              entityTypes: entity.entityTypes?.map((type: any) => ({
                id: parseInt(type.id),
                name: type.name,
              })),
            }),
          ),
        })),
      );
  }

  getProjectTemplatesViewModel(
    forceHttp = false,
  ): Observable<ITemplateViewModel> {
    return this._getProjectTemplatesViewModel(forceHttp);
  }

  private _getProjectTemplatesViewModel(
    forceHttp = false,
  ): Observable<ITemplateViewModel> {
    return this.useCache(
      this.api.post(`${this.controller}/GetProjectTemplatesViewModel`, {}).pipe(
        map((res) => ({
          projectTemplates: res.projectTemplates,
          projectDueDates: res.projectDueDates,
          recurrences: res.recurrences,
          entityTypes: res.entityTypes,
          customFieldDefinitions: res.customFieldDefinitions,
          emails: res.emails,
        })),
      ),
      {
        forceHttp,
      },
    );
  }

  getProjectTemplatesEmails(forceHttp = false) {
    return this.getProjectTemplatesViewModel(forceHttp).pipe(
      map((res) => res.emails),
    );
  }

  private _createProjectTemplateBody(template: ProjectTemplate) {
    return {
      projectTemplateId: template.projectTemplateId,
      name: template.name,
      dueDate: template.dueDate,
      recurrence: template.recurrence,
      isRecurrenceEditable: template.isRecurrenceEditable,
      isCopyable: template.isCopyable,
      isCancelable: template.isCancelable,
      isTasksDisabled: template.isTasksDisabled,
      isNotesDisabled: template.isNotesDisabled,
      isEmailDisabled: template.isEmailDisabled,
      isCustomFieldsDisabled: template.isCustomFieldsDisabled,
      isCustomFieldsAboveTasks: template.isCustomFieldsAboveTasks,
      isTaskGroupOnly: template.isTaskGroupOnly,
      isCancelReasonDisabled: template.isCancelReasonDisabled,
      isPublic: template.isPublic,
      propagateOnRecurrence: template.propagateOnRecurrence,
      isDeleted: template.isDeleted,
      createdBy: template.createdBy,
      customFieldDefinitionId: template.customFieldDefinitionId,
      hideNonReadyTasks: template.hideNonReadyTasks,
      accessGroupIds: template.accessGroups
        ?.map((group) => group.id)
        ?.join(','),
      creatorGroupIds: template.creatorGroups
        ?.map((group) => group.id)
        ?.join(','),
      adminGroupIds: template.adminGroups?.map((group) => group.id)?.join(','),
      completionGroupIds: template.completionGroups
        ?.map((group) => group.id)
        ?.join(','),
      defaultOwnerId: template.defaultOwnerId,
      recurrenceOwner: template.recurrenceOwner,
    };
  }

  createProjectTemplate(
    projectTemplate: ProjectTemplate,
  ): Observable<IServerResponse & { template?: ProjectTemplate }> {
    return this.api.post(
      `${this.controller}/CreateProjectTemplate`,
      this._createProjectTemplateBody(projectTemplate),
    );
  }

  updateTemplateDetails(
    details: ProjectTemplate,
  ): Observable<IServerResponse & { projectTemplate?: ProjectTemplate }> {
    return this.api.post(
      `${this.controller}/UpdateProjectTemplate`,
      this._createProjectTemplateBody(details),
    );
  }

  updateEntityRequirements(
    entities: EntityRequirement[],
  ): Observable<
    IServerResponse & { projectEntityRequirementsList?: EntityRequirement[] }
  > {
    const body = entities.map((er) => ({
      ...er,
      entityTypeIds: er.entityTypes?.map((et) => et.id)?.join(','),
    }));
    return this.api
      .post(`${this.controller}/UpdateTemplateEntityRequirements`, body)
      .pipe(
        map((res) => ({
          ...res,
          projectEntityRequirementsList: res.projectEntityRequirementsList.map(
            (e: any) => ({
              ...e,
              entityTypes: e.entityTypes?.map((type: any) => ({
                id: parseInt(type.id),
                name: type.name,
              })),
            }),
          ),
        })),
      );
  }

  private _createProjectTemplateTasksBody(
    templateTasks: ProjectTemplateTask[],
  ) {
    return templateTasks.map((templateTask) => ({
      projectTemplateTaskId: templateTask.projectTemplateTaskId,
      projectTemplateId: templateTask.projectTemplateId,
      name: templateTask.name,
      sortOrder: templateTask.sortOrder,
      dueDateOption: templateTask.dueDateOption,
      dueDateDays: templateTask.dueDateDays,
      customFieldDataDefinitionId: templateTask.customFieldDataDefinitionId,
      defaultOwnerId: templateTask.defaultOwnerId,
      recurrenceOwner: templateTask.recurrenceOwner,
      isMandatory: templateTask.isMandatory,
      isDeletable: templateTask.isDeletable,
      isClonable: templateTask.isClonable,
      isRecurring: templateTask.isRecurring,
      isRecurringOnly: templateTask.isRecurringOnly,
      isWrapUp: templateTask.isWrapUp,
      propagateOnRecurrence: templateTask.propagateOnRecurrence,
      description: templateTask.description,
      isDeleted: templateTask.isDeleted,
      systemKey: templateTask.systemKey,
      createdOn: templateTask.createdOn,
      createdBy: templateTask.createdBy,
      modifiedBy: templateTask.modifiedBy,
      relatedCustomFieldId: templateTask.relatedCustomFieldId,
      relatedCustomFieldListId: templateTask.relatedCustomFieldListId,

      completionGroups: templateTask.completionGroupsList
        ?.map((group) => group.id)
        ?.join(','),
      selectableGroups: templateTask.selectableGroupsList
        ?.map((group) => group.id)
        ?.join(','),
      alertOwnersOfTaskIds: templateTask.alertOwnersOfTaskIdsList
        ?.map((parent) => parent.id)
        ?.join(','),
      notifyEmailAddresses: templateTask.notifyEmailAddressesList
        ?.map((email) => email.email)
        ?.join(','),

      projectTaskParents: templateTask.projectTaskParents?.map(
        (parentTask) => ({
          ...parentTask,
          customFieldControl:
            (parentTask.customFieldDataDefinitionId ?? 0) > 0
              ? JSON.stringify({
                  customFieldDataDefinitionId:
                    parentTask.customFieldDataDefinitionId,
                  typeId: parentTask.customFieldDataTypeId,
                  value: parentTask.defaultValue,
                })
              : null,
        }),
      ),
    }));
  }

  updateTemplateTasks(
    templateTasks: ProjectTemplateTask[],
  ): Observable<
    IServerResponse & { templateTasksList: ProjectTemplateTask[] }
  > {
    return this.api.post(
      `${this.controller}/AddOrUpdateTemplateTasks`,
      this._createProjectTemplateTasksBody(templateTasks),
    );
  }
}
